/* eslint-disable no-await-in-loop */ // Effecting query/pattern usability

import { Dispatch } from 'react';

import {
  getRecaptchaResponse,
  getRecaptchaWidgetId,
  isRecaptchaEnabled,
} from '@/legacy/features/user/twoFactorAuthentication/recaptcha/recaptcha.helpers';
import * as UserService from '@/legacy/services/user';
import { UserLoginResponse } from '@/legacy/services/user';
import { Action, QueryState } from '@/legacy/utils/reactHelpers';

enum ActionType {
  SubmitForm = 'SubmitForm',
  UpdatePassword = 'UpdatePassword',
  UpdateErrorMessage = 'UpdateErrorMessage',
  Logout = 'Logout',
  UpdateRecaptchaWidgetId = 'UpdateRecaptchaWidgetId',
  StartFormSubmit = 'StartFormSubmit',

  ShowTwoFactorCodeDialog = 'ShowTwoFactorCodeDialog',
  CloseTwoFactorCodeDialog = 'CloseTwoFactorCodeDialog',

  ShowTwoFactorSignupDialog = 'ShowTwoFactorSignupDialog',
  CloseTwoFactorSignupDialog = 'CloseTwoFactorSignupDialog',

  QuerySubmitLoginForm = 'QuerySubmitLoginForm',
  QueryLogout = 'QueryLogout',
  QueryRecaptchaWidget = 'QueryRecaptchaWidget',
  QueryLoginSuccess = 'QueryLoginSuccess',
}

interface QuerySubmitLoginFormPayload {
  email: string;
  password: string;
  recaptchaWidgetId: string;
}

interface UpdateErrorMessagePayload {
  errorMessage: string;

  /**
   * If an error is "blocking" then the entire form is disabled (logout button still works)
   */
  isBlocking?: boolean;

  /**
   * If set to true clears the password
   */
  resetPassword?: boolean;
}

interface CloseTwoFactorCodeDialogPayload {
  /**
   * If true the 2FA code challenge was entered successfully
   * If false the user clicked back button in 2FA entry popup
   */
  isSuccessful: boolean;
}

interface LoginCallbacks {
  onLoginSuccess?: () => void;
  onLogout?: () => void;
}

interface State extends QueryState<ActionType> {
  email: string;
  password: string;
  errorMessage: string | null;
  isLoading: boolean;

  /**
   * This is reCAPTCHA widget id that is sent from QueryRecaptchaWidget query (on load)
   */
  recaptchaWidgetId?: string;

  isTwoFactorCodePopupOpen: boolean;
  isTwoFactorSignupOpen: boolean;
}
const getInitialState = (initializer?: Partial<State>): State => {
  const initialState: State = {
    email: '',
    password: '',
    errorMessage: null,
    isLoading: true, // By default we'll be loading until recaptcha is fully loaded
    isTwoFactorCodePopupOpen: false, // Shows when 2fa code is required
    isTwoFactorSignupOpen: false, // Shows when 2fa signup is required

    queries: [
      {
        type: ActionType.QueryRecaptchaWidget, // On load bind to recaptcha
      },
    ],
  };

  return {
    ...initialState,
    ...initializer,
  };
};

const localConfig = {
  /**
   * This maximum password length matches the backend binding model
   */
  maxPasswordLength: 100,
};

const reducer = (state: State, action: Action<ActionType>): State => {
  const { payload, type } = action;

  switch (type) {
    case ActionType.SubmitForm: {
      const { email, password, recaptchaWidgetId } = state;

      return {
        ...state,
        errorMessage: null, // Clear any errors

        queries: [
          {
            type: ActionType.QuerySubmitLoginForm,
            payload: {
              email,
              password,
              recaptchaWidgetId,
            } as QuerySubmitLoginFormPayload,
          },
        ],
      };
    }

    case ActionType.UpdatePassword: {
      if (payload.length >= localConfig.maxPasswordLength) {
        return state;
      }

      return {
        ...state,
        password: payload as string,
        errorMessage: null, // Clear any errors
      };
    }

    case ActionType.ShowTwoFactorCodeDialog:
      return {
        ...state,
        isTwoFactorCodePopupOpen: true,
        password: '',
        isLoading: false,
      };

    case ActionType.CloseTwoFactorCodeDialog: {
      const { isSuccessful } = payload as CloseTwoFactorCodeDialogPayload;
      const getSuccessQuery = () => {
        if (!isSuccessful) {
          return {};
        }

        // If user entered 2FA code then we'll close the code and callback props.onLoginSuccess
        return {
          queries: [
            {
              type: ActionType.QueryLoginSuccess,
            },
          ],
        };
      };

      return {
        ...state,
        isTwoFactorCodePopupOpen: isSuccessful, // If it's successful we'll show the popup until the whole dialog is closed
        ...getSuccessQuery(),
      };
    }

    case ActionType.ShowTwoFactorSignupDialog:
      return {
        ...state,
        isTwoFactorSignupOpen: true,
        password: '',
        isLoading: false,
      };

    case ActionType.CloseTwoFactorSignupDialog:
      return {
        ...state,
        isTwoFactorSignupOpen: false,
      };

    case ActionType.UpdateErrorMessage: {
      const { errorMessage, isBlocking, resetPassword } = payload as UpdateErrorMessagePayload;

      return {
        ...state,
        errorMessage,
        password: resetPassword ? '' : state.password, // Optionally clears password entry on error
        isLoading: isBlocking, // Re-enables form (if non-blocking)
      };
    }
    case ActionType.UpdateRecaptchaWidgetId: {
      return {
        ...state,
        recaptchaWidgetId: payload as string,
        isLoading: false,
      };
    }

    case ActionType.StartFormSubmit: {
      return {
        ...state,
        isLoading: true,
      };
    }

    case ActionType.Logout: {
      return {
        ...state,
        queries: [{ type: ActionType.QueryLogout }],
      };
    }

    default:
      return { ...state };
  }
};

const handleQueries = async (
  queries: Array<Action<ActionType>>,
  dispatch: Dispatch<Action<ActionType>>,
  callbacks: LoginCallbacks
): Promise<void> => {
  const showPasswordError = ({
    errorMessage,
    isBlocking = false,
    resetPassword = true,
  }: UpdateErrorMessagePayload) => {
    // @todo figure out how to focus on error field
    dispatch({
      type: ActionType.UpdateErrorMessage,
      payload: {
        errorMessage,
        isBlocking,
        resetPassword,
      },
    });
  };

  for (const { type, payload } of queries) {
    switch (type) {
      case ActionType.QueryRecaptchaWidget: {
        // If we fail loading recaptcha or for some reason element isn't in DOM block form.
        const recaptchaElement = document.getElementById('sessionTimeout-recaptcha');
        if (!recaptchaElement) {
          showPasswordError({
            errorMessage: 'Failed loading Google reCAPTCHA. Please logout to continue.',
            isBlocking: true,
          });
          break;
        }

        try {
          const recaptchaWidgetId = await getRecaptchaWidgetId(recaptchaElement, () => {
            // Re-submit the form (which will trigger QuerySubmitLoginForm again)
            dispatch({
              type: ActionType.SubmitForm,
            });
          });

          dispatch({
            type: ActionType.UpdateRecaptchaWidgetId,
            payload: recaptchaWidgetId,
          });
        } catch (err) {
          showPasswordError({
            errorMessage: 'Failed binding Google reCAPTCHA. Please logout to continue.',
            isBlocking: true,
          });
          break;
        }

        break;
      }
      case ActionType.QueryLogout: {
        if (callbacks.onLogout) {
          callbacks.onLogout();
        }
        break;
      }
      case ActionType.QueryLoginSuccess: {
        if (callbacks.onLoginSuccess) {
          callbacks.onLoginSuccess();
        }
        break;
      }

      case ActionType.QuerySubmitLoginForm: {
        try {
          const { email, password, recaptchaWidgetId } = payload as QuerySubmitLoginFormPayload;

          const recaptchaV2Response = await getRecaptchaResponse(recaptchaWidgetId);
          if (!recaptchaV2Response) {
            return;
          }

          // If we get here the user has successfully completed recaptcha
          dispatch({
            type: ActionType.StartFormSubmit,
          });

          const userLoginResult = await UserService.Instance.Login({
            email,
            passphrase: password,
            recaptchaV2Response,
          });

          const userLoginResponse = userLoginResult.userLoginResponse as UserLoginResponse;
          switch (userLoginResponse) {
            case UserLoginResponse.TwoFactorAuthenticationRequired:
              dispatch({
                type: ActionType.ShowTwoFactorCodeDialog,
              });
              break;
            case UserLoginResponse.Successful:
              if (callbacks.onLoginSuccess) {
                callbacks.onLoginSuccess();
              }
              break;
            case UserLoginResponse.TwoFactorComplianceRequired:
            case UserLoginResponse.TwoFactorSignupRequired:
              dispatch({
                type: ActionType.ShowTwoFactorSignupDialog,
              });
              break;
          }
        } catch (err) {
          if (!err || !err.error) {
            showPasswordError({
              errorMessage: 'Could not connect to Plooto login server. Please try again later.',
            });
            return;
          }

          switch (err.type) {
            case 'invalid.user':
              showPasswordError({
                errorMessage: 'Incorrect password. Please try again.',
              });
              break;
            case 'invalid.recaptchav2':
            case 'incorrect.captcha':
              showPasswordError({
                errorMessage: 'Incorrect captcha response. Please try again.',
              });
              break;
            case 'rateLimit.login':
              showPasswordError({
                errorMessage: 'Login rate limited exceeded. Please try again later.',
              });
              break;

            default:
              showPasswordError({
                errorMessage: 'Unexpected login error. Please try again later.',
              });
              break;
          }

          // Attempt to reset recaptcha on any error
          try {
            if (isRecaptchaEnabled()) {
              window.grecaptcha.reset();
            }
          } catch (recaptchaErr) {
            // Silently handled
          }
        }
      }
    }
  }
};

export { State, ActionType, getInitialState, handleQueries, reducer, LoginCallbacks };
